import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { FlagModal } from './FlagModal.jsx';
import { periodColor } from '../lib/ui.js';
import { todayStr } from '../lib/format.js';

// ─── Recent activity (Yesterday + back, plus Today) ──────────────────────────
// Date-paginated view that shows completed/missed tasks for any day in the
// last ~30 days. Defaults to TODAY so a closer can dispute or undo an
// opener's same-day completion. Prev/Today/Next buttons step the date.
export function YesterdayView({ locationId, userId, isManager, timePeriods }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [flagOpen, setFlagOpen] = useState(null);
  const [flaggedIds, setFlaggedIds] = useState(new Set());
  // Default to today (so the surface is useful mid-shift). User can step
  // backwards with "← Prev" to review yesterday or earlier.
  const todayYmd = todayStr();
  const [date, setDate] = useState(todayYmd);

  // D104: load() had no try/catch and no finally. A throw skipped
  // setLoading(false) entirely, leaving loading true and data null — so the
  // gate below returned the spinner forever, plus an unhandled rejection on
  // the way past. Clear the flag in finally so the gate always opens, and keep
  // any already-loaded day on screen rather than blanking it on a failed step.
  async function load() {
    setLoading(true);
    setErr('');
    try {
      const d = await api('GET', `/api/tasks/${locationId}/yesterday?date=${date}`);
      setData(d);
      setFlaggedIds(new Set());
    } catch (e) {
      let m = e.message || String(e);
      try { m = JSON.parse(m).error || m; } catch { /* not JSON */ }
      setErr(m);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [locationId, date]);

  function shiftDay(days) {
    const d = new Date(date + 'T12:00:00Z');
    d.setUTCDate(d.getUTCDate() + days);
    const next = d.toISOString().slice(0, 10);
    if (next > todayYmd) return;   // cap at today; future days are still pending
    setDate(next);
  }

  async function undo(task) {
    if (!confirm('Undo this completion? It will return to the open list.')) return;
    try {
      await api('DELETE', `/api/task-completions/${task.completion_id}`, null,
        { 'x-user-id': String(userId) });
      load();
    } catch (e) {
      let m = e.message; try { m = JSON.parse(e.message).error; } catch {}
      alert(`Undo failed: ${m}`);
    }
  }

  if (loading && !data) return <div className="flex items-center justify-center h-40 text-gray-400">Loading…</div>;
  if (err && !data) {
    return (
      <div className="card" style={{ borderLeft: '4px solid #c62828' }}>
        <div className="text-sm" style={{ fontWeight: 600, marginBottom: 4 }}>Couldn’t load this day</div>
        <div className="text-xs text-red-700" style={{ marginBottom: 10 }}>{err}</div>
        <button className="btn-secondary text-xs" onClick={load}>Try again</button>
      </div>
    );
  }
  if (!data) return null;

  const activePeriods = [
    { id: 0, name: 'Anytime', sort_order: 0, isAnytime: true },
    ...(timePeriods.length > 0 ? timePeriods : [
      {id:0,name:'Opening',sort_order:1},{id:0,name:'Morning',sort_order:2},
      {id:0,name:'Midday',sort_order:3},{id:0,name:'Afternoon',sort_order:4},{id:0,name:'Closing',sort_order:5}
    ]),
  ];
  function periodMatches(t, name) {
    const tp = (t.effective_period || t.time_period || '').trim();
    if (name === 'Anytime') return !tp || /^(anytime|any|all\s*day)$/i.test(tp);
    return tp.toLowerCase() === name.toLowerCase();
  }
  const byPeriod = activePeriods.reduce((acc, p) => {
    acc[p.name] = data.tasks.filter(t => periodMatches(t, p.name));
    return acc;
  }, {});

  const completed = data.tasks.filter(t => t.status === 'completed').length;
  const total = data.tasks.length;
  const missed = total - completed;
  const d = new Date(data.date + 'T12:00:00');
  const dateLabel = d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <div className="space-y-6">
      {flagOpen && (
        <FlagModal task={flagOpen} locationId={locationId} userId={userId}
          onClose={() => setFlagOpen(null)}
          onSubmit={() => { setFlaggedIds(s => new Set([...s, flagOpen.id])); setFlagOpen(null); load(); }} />
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        {/* Date nav — same prev/today/next pattern as Timesheets so the
            mental model is consistent. Disabled "Next" once we hit today
            since future days don't have completions yet. */}
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-widest">{dateLabel}</p>
            {date === todayYmd && <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5 inline-block mt-1">Today</span>}
          </div>
          <div className="flex gap-2">
            <button onClick={() => shiftDay(-1)}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 hover:bg-gray-50">
              ← Prev
            </button>
            <button onClick={() => setDate(todayYmd)} disabled={date === todayYmd}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gold/10 text-yellow-800 border border-gold/30 hover:bg-gold/20 disabled:opacity-40">
              Today
            </button>
            <button onClick={() => shiftDay(1)} disabled={date >= todayYmd}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40">
              Next →
            </button>
          </div>
        </div>
        <div className="flex gap-8">
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">{completed}</p>
            <p className="text-xs text-gray-500">Completed</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-red-500">{missed}</p>
            <p className="text-xs text-gray-500">{date === todayYmd ? 'Pending' : 'Missed'}</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-ink">{total > 0 ? Math.round(completed / total * 100) : 0}%</p>
            <p className="text-xs text-gray-500">Rate</p>
          </div>
        </div>
      </div>

      {activePeriods.map((periodObj, pidx) => {
        const period = periodObj.name;
        const periodTasks = byPeriod[period] || [];
        if (!periodTasks.length) return null;
        const doneCount = periodTasks.filter(t => t.status === 'completed').length;
        return (
          <div key={period}>
            <div className="flex items-center gap-3 mb-3">
              <span className={`period-badge px-2 py-1 rounded-md ${periodColor(period, pidx)}`}>{period}</span>
              <span className="ml-auto text-xs text-gray-400 font-medium">{doneCount}/{periodTasks.length}</span>
            </div>
            <div className="space-y-2">
              {periodTasks.map(task => {
                const done = task.status === 'completed';
                const alreadyFlagged = flaggedIds.has(task.id) || task.flagged;
                return (
                  <div key={task.id} className={`rounded-xl border-l-4 p-4 ${done ? 'border-l-green-500 bg-green-50' : 'border-l-red-400 bg-red-50'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full flex-shrink-0 ${done ? 'bg-green-500' : 'bg-red-400'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-ink">{task.title}</p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <span className="text-xs text-gray-500">{task.assigned_role}</span>
                          {task.due_time && <span className="text-xs text-gray-400">· {task.due_time}</span>}
                          {done && <span className="text-xs text-green-600 font-medium">· ✓ Completed</span>}
                          {!done && <span className="text-xs text-red-500 font-medium">· Not completed</span>}
                          {done && isManager && task.completed_by_name && (
                            <span className="text-xs text-gray-400">· by {task.completed_by_name}</span>
                          )}
                        </div>
                      </div>
                      {done && !alreadyFlagged && (task.completed_by === userId || isManager) && (
                        <button onClick={async () => {
                          if (!confirm('Undo this completion? It will return to the open list and the original "completed by" name will be cleared.')) return;
                          try {
                            await api('DELETE', `/api/task-completions/${task.completion_id}`, null, { 'x-user-id': String(userId) });
                            load();
                          } catch (e) {
                            let m = e.message; try { m = JSON.parse(e.message).error; } catch {}
                            alert(`Undo failed: ${m}`);
                          }
                        }} className="flex-shrink-0 px-3 py-1.5 bg-amber-50 text-amber-700 text-xs font-semibold rounded-lg hover:bg-amber-100 border border-amber-200 active:scale-95 transition-all">
                          Undo
                        </button>
                      )}
                      {done && !alreadyFlagged && (
                        <button onClick={() => setFlagOpen(task)}
                          className="flex-shrink-0 px-3 py-1.5 bg-red-50 text-red-600 text-xs font-semibold rounded-lg hover:bg-red-100 border border-red-200 active:scale-95 transition-all">
                          Report Issue
                        </button>
                      )}
                      {alreadyFlagged && (
                        <span className="flex-shrink-0 text-xs bg-red-100 text-red-700 px-2 py-1 rounded-lg font-medium border border-red-200">🚩 Reported</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {total === 0 && (
        <div className="text-center py-16 text-gray-400">
          <p className="text-4xl mb-3">📋</p>
          <p className="font-medium">No tasks on record for yesterday.</p>
        </div>
      )}
    </div>
  );
}
